// Open an intent from the user's wallet (docs/builders-ui.md §2–6). The open builds exactly the quote
// the user confirmed. A solver's quote is live for `valid_for_ms` (a few seconds) and `/build`
// refuses a stale one; only then is a fresh quote taken, and it replaces the confirmed one without
// asking only if it guarantees at least as much (`PriceChanged` otherwise). After `/build` the
// signed open has no deadline of its own, so an approval may take its time: what was built is sent.
// A non-stable input goes through the api's hosted swap (§4): one router call on EVM, the
// Jupiter route composed with the open on Solana; the escrow, and any refund, is the stable.
// Jupnet escrows one of its stables (no swap there); the api co-signs and pays for the open, and
// the user's Jupnet key (`jupnet-key.ts`) signs it.

import { Keypair, Transaction, VersionedTransaction } from "@solana/web3.js";
import { awaitRegistered, build, isPriceMoved, firstValue, intentsOf, pickBest, quoteRound, registerOpen, submit, type Built, type Delivery, type Quote, type QuoteHead } from "./api";
import { isNativeEvm, isStable, type Chain, type Token } from "./chains";
import {
  approveData,
  batchFallbackAllowed,
  batchIntentHash,
  ensureChain,
  erc20Allowance,
  erc20Balance,
  ethBalance,
  evmPath,
  openedIntentHash,
  permitCalldata,
  postedPermit,
  sendBatch,
  sendTx,
  signTypedData,
  simulate,
  waitReceipt,
  walletAtomic,
  type CallsStatus,
  type Eip1193,
  type EvmPath,
} from "./evm";
import { GUM_JUPNET_PROGRAM, checkJupnetOpen, jupnetSigning, recipientRaw, signJupnetOpen } from "./jupnet";
import { builtTxs, openedFromLogs, rowForOpenTx, txLogs } from "./solana";
import { guaranteed, notWorse } from "./terms";
import { fromBaseUnits } from "./validate";

type SolTx = Transaction | VersionedTransaction;

export type OpenParams = {
  api: string;
  src: Chain;
  dst: Chain;
  /** what the user pays: the chain's stable, or any token the api swaps into it */
  inputToken: Token;
  dstToken: Token;
  /** gross in `inputToken`, smallest units */
  amount: string;
  /** Jupnet, paid from several stablecoins (a USD basket): each and its gross, smallest units;
   *  `inputToken` / `amount` then describe the first */
  inputs?: { token: Token; amount: string }[];
  recipient: string;
  /** `gum_deposit`: `recipient` is a Jupnet account, credited through gum's bank on `dst` */
  delivery?: Delivery;
  slippageBps: number;
  /** the source swap's tolerance (hosted swap only; the api's default otherwise) */
  srcSlippageBps?: number;
  /** the source wallet's address (Jupnet: the Jupnet key's) */
  user: string;
  /** the quote round and solver quote the user confirmed: what the open builds */
  confirmed: { head: QuoteHead; quote: Quote };
  evm?: Eip1193;
  solana?: { signTransaction: <T extends SolTx>(tx: T) => Promise<T>; signAllTransactions?: <T extends SolTx>(txs: T[]) => Promise<T[]> };
  solanaRpc?: string;
  /** gum-intent's program id, whose log names a Solana open's intent (default GUM_PROGRAM) */
  solanaProgram?: string;
  /** the user's Jupnet key: signs a Jupnet open */
  jupnetKey?: Keypair;
  /** gum-intent's Jupnet program id, the only program a Jupnet open may call (default GUM_JUPNET_PROGRAM) */
  jupnetProgram?: string;
  /** the connected Sui wallet: signs the api's built open, returns `/submit`'s payload */
  sui?: { sign: (txBytesBase64: string) => Promise<string> };
  log: (msg: string) => void;
  /** where the open is, for the UI's button and stepper */
  phase?: (p: OpenPhase) => void;
};
/** `switch` the wallet's network, `build` the quote, `approve` the input (a transaction, a permit
 *  or a batch), `sign` the open, `submit` it, `confirm` it landed and find its intent. */
export type OpenPhase = "switch" | "build" | "approve" | "sign" | "submit" | "confirm";
export type Opened = { intentHash: string; openTx: string; quote: Quote; head: QuoteHead; built: Built; path?: EvmPath | "bundle" | "single" };

export const NO_QUOTE = "No solver quoted this pair. Solvers send no reason; try another pair or amount.";
export const NO_SOLVER = "No solver is connected to the api right now, so nobody can quote. Try again shortly.";
export const noQuoteMessage = (solversAsked: number) => (solversAsked === 0 ? NO_SOLVER : NO_QUOTE);

/** What each EVM path means to the user, for the log. */
export const PATH_LABEL: Record<EvmPath, string> = {
  none: "no approval needed",
  batch: "approve + open in one wallet batch (EIP-5792)",
  permit: "gasless permit signature, then one transaction",
  approve: "approval transaction, then the open",
};

async function freshQuote(p: OpenParams): Promise<{ head: QuoteHead; best: Quote }> {
  const swap = !isStable(p.src, p.inputToken.addr);
  const { head, quotes } = await quoteRound(p.api, {
    src_chain: p.src.key,
    dst_chain: p.dst.key,
    ...(p.inputs ? { inputs: p.inputs.map((i) => ({ token: i.token.addr, amount: i.amount })) } : { input_token: p.inputToken.addr, input_amount: p.amount }),
    dst_token: p.dstToken.addr,
    dst_recipient: p.recipient,
    ...(swap && p.srcSlippageBps !== undefined ? { src_slippage_bps: p.srcSlippageBps } : {}),
    ...(p.delivery === "gum_deposit" ? { delivery: p.delivery } : {}),
  });
  const best = pickBest(quotes);
  if (!best) throw new Error(noQuoteMessage(head.solvers_asked));
  return { head, best };
}

/** Everything an open is built from, as one comparable value: a quote round is only usable for
 *  the form it was taken for. */
export function formKey(f: { src: string; dst: string; inputToken: string; amount: string | null; srcSlippage?: number; dstToken: string; recipient: string; delivery?: Delivery; slippage: number }): string {
  return JSON.stringify([f.src, f.dst, f.inputToken, f.amount, f.srcSlippage ?? null, f.dstToken, f.recipient, f.delivery ?? "direct", f.slippage]);
}

/** Whether a quote round taken for the form `taken` may be shown (or opened) for the form as it is
 *  `now`: only for the very same inputs. A replacement that arrives after the user edited the form
 *  (e.g. a PriceChanged quote while they typed a new recipient) is dropped. */
export const quoteFitsForm = (taken: string, now: string): boolean => taken === now;

export type QuoteState = { head: QuoteHead; quotes: Quote[]; done: boolean; key: string };

/** What a PriceChanged open leaves on screen: the fresh quote, if the form is still the one the
 *  open was taken for (`taken` vs `current`), else the form's own state (`kept`). */
export function afterPriceChanged(e: PriceChanged, taken: string, current: string, kept: QuoteState | null): QuoteState | null {
  return quoteFitsForm(taken, current) ? { head: e.fresh.head, quotes: [e.fresh.quote], done: true, key: taken } : kept;
}

/** The quote on screen after a finished round. A background refresh that brought no quote keeps
 *  the previous one (its error is shown, and the next refresh is scheduled); a round the user asked
 *  for shows what it got, empty or not. */
export function afterRound(prev: QuoteState | null, round: QuoteState | null, refresh: boolean): QuoteState | null {
  if (refresh && (!round || !round.quotes.length) && prev?.quotes.length) return prev;
  return round;
}

/** The confirmed quote can no longer be built and a fresh one guarantees less: nothing was signed;
 *  show `fresh` and let the user decide. */
export class PriceChanged extends Error {
  constructor(
    readonly was: bigint,
    readonly now: bigint,
    readonly fresh: { head: QuoteHead; quote: Quote },
  ) {
    super("the price changed since you confirmed");
  }
}

/** A build refused because the confirmed quote lapsed or the swap's price moved. */
const lapsed = (e: unknown) => isPriceMoved(e) || /no live quote/i.test(String((e as Error)?.message));

/** Build the quote the user confirmed. If it lapsed, a fresh quote is built instead only if it
 *  guarantees at least as much; otherwise `PriceChanged`, before anything is signed. */
async function buildConfirmed(p: OpenParams): Promise<{ head: QuoteHead; best: Quote; built: Built }> {
  p.phase?.("build");
  const want = guaranteed(p.confirmed.head, p.confirmed.quote, p.slippageBps);
  const buildOf = (head: QuoteHead, q: Quote) => build(p.api, { quote_id: head.quote_id, solver: q.solver, dst_amount: q.dst_amount, slippage_bps: p.slippageBps, user: p.user });
  let head = p.confirmed.head;
  let best = p.confirmed.quote;
  let built: Built;
  try {
    built = await buildOf(head, best);
  } catch (e) {
    if (!lapsed(e)) throw e;
    p.log(isPriceMoved(e) ? "the swap's price moved since you confirmed; checking a fresh quote…" : "the quote you confirmed lapsed; checking a fresh one…");
    const fresh = await freshQuote(p);
    const got = guaranteed(fresh.head, fresh.best, p.slippageBps);
    if (!notWorse(want, got)) throw new PriceChanged(want, got, { head: fresh.head, quote: fresh.best });
    p.log(`a fresh quote guarantees at least ${fromBaseUnits(got.toString(), p.dstToken.decimals)} ${p.dstToken.sym}, not less than you confirmed: continuing with it`);
    ({ head, best } = fresh);
    built = await buildOf(head, best);
  }
  if (built.tx.vm !== p.src.family) throw new Error(`the api built a ${built.tx.vm} transaction for a ${p.src.label} source`);
  // the signed delivery decides where the output goes: never sign one the user did not choose
  if ((built.args?.delivery ?? 0) !== (p.delivery === "gum_deposit" ? 1 : 0)) throw new Error(`the api built delivery ${built.args?.delivery} for a ${p.delivery ?? "direct"} open; nothing was signed`);
  p.log(`quoted by ${best.solver}: ${fromBaseUnits(best.dst_amount, p.dstToken.decimals)} ${p.dstToken.sym}, floor ${fromBaseUnits(built.min_dst_amount, p.dstToken.decimals)}`);
  if (head.source_swap) p.log(`swapping ${fromBaseUnits(p.amount, p.inputToken.decimals)} ${p.inputToken.sym} → at least ${fromBaseUnits(built.source_swap?.min_out ?? head.source_swap.min_out, p.src.stable.decimals)} ${p.src.stable.sym} (${head.source_swap.aggregator})`);
  return { head, best, built };
}

export async function openIntent(p: OpenParams): Promise<Opened> {
  if (p.src.family === "evm") return openEvm(p);
  if (p.src.family === "solana") return openSolana(p);
  if (p.src.family === "sui") return openSui(p);
  if (p.src.family === "jupnet") return openJupnet(p);
  throw new Error(`${p.src.label} is not supported as a source yet`);
}

async function openEvm(p: OpenParams): Promise<Opened> {
  const w = p.evm;
  if (!w) throw new Error("connect an EVM wallet first");
  const chainId = p.src.evmChainId!;
  p.phase?.("switch");
  await ensureChain(w, chainId, p.src.label, p.src.addChain);
  const native = p.inputToken.native || isNativeEvm(p.inputToken.addr);
  const bal = native ? await ethBalance(w, p.user) : await erc20Balance(w, p.inputToken.addr, p.user);
  if (bal < BigInt(p.amount)) throw new Error(`insufficient ${p.inputToken.sym}: you have ${fromBaseUnits(bal, p.inputToken.decimals)} on ${p.src.label}`);

  const { head, best, built } = await buildConfirmed(p);
  if (!head.source_swap) return openEvmStable(p, w, head, best, built);

  // hosted swap: the router pulls the token, so its approval (if any) is of the router
  let path = evmPath(built, built.approval ? await walletAtomic(w, p.user, chainId) : false);
  p.log(`approval: ${PATH_LABEL[path]}`);
  const evmTx = (b: Built) => {
    if (b.tx.vm !== "evm") throw new Error("not an EVM build");
    return { to: b.tx.to, data: b.tx.data, value: BigInt(b.tx.value) };
  };

  if (path === "batch") {
    let st: CallsStatus | null = null;
    try {
      p.log("confirm approve + open in your wallet…");
      p.phase?.("approve");
      st = await sendBatch(w, p.user, chainId, [{ to: built.approval!.tx.to, data: built.approval!.tx.data }, evmTx(built)]);
    } catch (e: any) {
      // once the wallet took the batch it may still open: never send a second one
      if (!batchFallbackAllowed(e)) throw e;
      // a wallet that announced the capability and then refused the batch: fall through
      path = built.permit ? "permit" : "approve";
      p.log(`the wallet did not take the batch (${e?.message ?? e}); falling back to: ${PATH_LABEL[path]}`);
    }
    if (st) {
      // the receipts carry the router's event; else the api finds the open from the transaction
      const intentHash = batchIntentHash(st.receipts, evmTx(built).to) ?? (st.txHash ? await awaitRegistered(p.api, p.src.key, st.txHash) : null);
      if (!intentHash) throw new Error(`the batch confirmed but its open was not found${st.txHash ? ` (${st.txHash})` : ""}; look the transaction up rather than opening again`);
      if (st.txHash) void registerOpen(p.api, p.src.key, st.txHash).catch(() => null);
      return { intentHash, openTx: st.txHash ?? "", quote: best, head, built, path };
    }
  }
  let open = evmTx(built);
  if (path === "permit") {
    p.log("sign the permit in your wallet (no gas)…");
    p.phase?.("approve");
    const permit = postedPermit(built.permit!, await signTypedData(w, p.user, built.permit!.typed_data));
    // the permit variant of the very call that was built: same swap, same terms, same quote
    open = { ...open, data: permitCalldata(open.data, permit) };
  } else if (path === "approve") {
    const a = built.approval!;
    p.log(`approve ${fromBaseUnits(a.amount, p.inputToken.decimals)} ${p.inputToken.sym} for the swap router in your wallet…`);
    p.phase?.("approve");
    const ah = await sendTx(w, p.user, { to: a.tx.to, data: a.tx.data }, "approval");
    p.log(`approval sent (${ah}); waiting for it to confirm…`);
    await waitReceipt(w, ah);
    // then the open that was built: no new quote, so the terms are the ones confirmed (if the
    // swap's price moved out of the band meanwhile, the simulation below says so before sending)
  }
  const h = await sendOpen(p, w, open);
  const intentHash = await intentOf(p, w, h, open.to);
  if (!intentHash) throw new Error(`no SwapOpened event in ${h}`);
  return { intentHash, openTx: h, quote: best, head, built, path };
}

/** The stable itself: approve the proxy (`tx.to`) if short, then open. */
async function openEvmStable(p: OpenParams, w: Eip1193, head: QuoteHead, best: Quote, built: Built): Promise<Opened> {
  if (built.tx.vm !== "evm") throw new Error("not an EVM build");
  const tx = built.tx;
  const allowance = await erc20Allowance(w, p.src.stable.addr, p.user, tx.to);
  if (allowance < BigInt(p.amount)) {
    p.log(`approve ${fromBaseUnits(p.amount, p.src.stable.decimals)} ${p.src.stable.sym} in your wallet…`);
    p.phase?.("approve");
    const ah = await sendTx(w, p.user, { to: p.src.stable.addr, data: approveData(tx.to, BigInt(p.amount)) }, "approval");
    p.log(`approval sent (${ah}); waiting for it to confirm…`);
    await waitReceipt(w, ah);
  }
  const h = await sendOpen(p, w, { to: tx.to, data: tx.data, value: BigInt(tx.value) });
  const intentHash = await intentOf(p, w, h, tx.to);
  if (!intentHash) throw new Error(`no Opened event in ${h}`);
  return { intentHash, openTx: h, quote: best, head, built };
}

/** The api re-reads the open from chain as soon as it lands (`POST /intents`); the receipt's event
 *  is the fallback. Whichever names the intent first starts the tracker. */
function intentOf(p: OpenParams, w: Eip1193, h: `0x${string}`, to: string): Promise<string | null> {
  const viaApi = awaitRegistered(p.api, p.src.key, h);
  const viaReceipt = waitReceipt(w, h).then((rc) => openedIntentHash(rc, to));
  return firstValue([viaApi, viaReceipt]);
}

async function sendOpen(p: OpenParams, w: Eip1193, open: { to: string; data: `0x${string}`; value: bigint }): Promise<`0x${string}`> {
  try {
    await simulate(w, p.user, open);
  } catch (e: any) {
    throw new Error(`the open would revert (${e?.message ?? e}); get a new quote and retry`);
  }
  p.log("confirm the open in your wallet…");
  p.phase?.("sign");
  const h = await sendTx(w, p.user, open, "open");
  p.log(`open sent (${h}); waiting for it to land…`);
  p.phase?.("confirm");
  return h;
}

const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const toB64 = (b: Uint8Array) => btoa(Array.from(b, (x) => String.fromCharCode(x)).join(""));

async function openSolana(p: OpenParams): Promise<Opened> {
  const w = p.solana;
  if (!w) throw new Error("connect a Solana wallet first");
  const { head, best, built } = await buildConfirmed(p);
  if (built.tx.vm !== "solana") throw new Error("not a Solana build");

  // the plain open: the api's Ed25519 authorisation, then open_intent (legacy). A hosted swap: v0
  // transaction(s) with Jupiter's route in front. The user is fee payer and only signer of each.
  const plan = builtTxs(built.tx);
  const txs: SolTx[] = plan.txs.map((b) => (plan.versioned ? VersionedTransaction.deserialize(fromB64(b)) : Transaction.from(fromB64(b))));
  if (plan.bundle && !w.signAllTransactions) throw new Error("this swap needs two transactions signed together, and your wallet cannot sign several at once");
  p.log(plan.bundle ? "sign the swap and the open (2 transactions, landed together) in your wallet…" : "sign the open in your wallet…");
  p.phase?.("sign");
  let signed: SolTx[];
  try {
    signed = plan.bundle ? await w.signAllTransactions!(txs) : [await w.signTransaction(txs[0]!)];
  } catch (e: any) {
    if (/reject|denied|cancel/i.test(String(e?.message ?? e))) throw new Error("you rejected the open in your wallet");
    throw new Error(`signing: ${e?.message ?? e}`);
  }
  const b64s = signed.map((t) => toB64(t.serialize()));
  p.phase?.("submit");
  const { tx: sig, intentHash: submitted } = await submit(p.api, "solana", plan.bundle ? b64s : b64s[0]!);
  const path = plan.versioned ? (plan.bundle ? "bundle" : "single") : undefined;
  // the api re-read the confirmed open and named its intent: track it now
  if (submitted) return { intentHash: submitted, openTx: sig, quote: best, head, built, path };
  p.log(`open sent (${sig}); finding its intent…`);
  p.phase?.("confirm");

  // the program's log has the hash at once; the api has it once it registers the open
  for (let i = 0; i < 90; i++) {
    const reg = await registerOpen(p.api, "solana", sig).catch(() => null);
    if (reg?.kind === "indexed") return { intentHash: reg.intentHash, openTx: sig, quote: best, head, built, path };
    const logs = await txLogs(p.solanaRpc, sig);
    const ev = logs && openedFromLogs(logs, p.solanaProgram);
    if (ev) return { intentHash: ev.intentHash, openTx: sig, quote: best, head, built, path };
    const row = rowForOpenTx(await intentsOf(p.api, p.user, "solana").catch(() => []), sig);
    if (row) return { intentHash: row.intent_hash, openTx: sig, quote: best, head, built, path };
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error(`could not find the intent for ${sig}; it may still land, check the explorer and track it by hash`);
}

/** Sui: the wallet signs the open the api built (sender, gas and commands already set) and the api
 *  executes it; `/submit` names the intent once its short wait sees the open, else `POST /intents`. */
async function openSui(p: OpenParams): Promise<Opened> {
  const w = p.sui;
  if (!w) throw new Error("connect a Sui wallet first");
  if (!isStable(p.src, p.inputToken.addr)) throw new Error(`${p.src.label} takes ${p.src.stable.sym} only: the api has no hosted swap there`);
  const { head, best, built } = await buildConfirmed(p);
  if (built.tx.vm !== "sui") throw new Error("not a Sui build");
  p.log("sign the open in your wallet…");
  p.phase?.("sign");
  let payload: string;
  try {
    payload = await w.sign(built.tx.tx_bytes_base64);
  } catch (e: any) {
    if (/reject|denied|cancel/i.test(String(e?.message ?? e))) throw new Error("you rejected the open in your wallet");
    throw new Error(`signing: ${e?.message ?? e}`);
  }
  p.phase?.("submit");
  const { tx: digest, intentHash: submitted } = await submit(p.api, "sui", payload);
  if (submitted) return { intentHash: submitted, openTx: digest, quote: best, head, built };
  p.log(`open sent (${digest}); finding its intent…`);
  p.phase?.("confirm");
  const intentHash = await awaitRegistered(p.api, "sui", digest);
  if (!intentHash) throw new Error(`could not find the intent for ${digest}; it may still land, check the explorer and track it by hash`);
  return { intentHash, openTx: digest, quote: best, head, built };
}

/** Jupnet: the api built the open; it signs as fee payer and `opener` at `/submit`. The user's
 *  Jupnet key signs its message as it is; the signature goes into the user's slot, is checked over
 *  the api's exact message, and the api co-signs and broadcasts the whole. */
async function openJupnet(p: OpenParams): Promise<Opened> {
  const key = p.jupnetKey;
  if (!key) throw new Error("set up a Jupnet key first: it owns the escrow and signs the open");
  if ((p.inputs?.map((i) => i.token) ?? [p.inputToken]).some((t) => !isStable(p.src, t.addr))) throw new Error(`${p.src.label} takes its stablecoins only: the api has no hosted swap there`);
  const { head, best, built } = await buildConfirmed(p);
  if (built.tx.vm !== "jupnet") throw new Error("not a Jupnet build");
  // the user's key signs these bytes with no wallet to show them: hold them against the request
  checkJupnetOpen(fromB64(built.tx.message_base64), {
    program: p.jupnetProgram ?? GUM_JUPNET_PROGRAM,
    user: p.user,
    inputMint: p.inputToken.addr,
    amount: p.amount,
    minGross: head.min_gross ?? head.gross,
    maxGross: head.max_gross ?? head.gross,
    dstChainId: p.dst.gumChainId,
    dstToken: p.dstToken.addr,
    dstFamily: p.dst.family,
    dstRecipient: recipientRaw(p.delivery === "gum_deposit" ? "jupnet" : p.dst.family, p.recipient),
    delivery: p.delivery === "gum_deposit" ? 1 : 0,
    rateNum: best.dst_amount,
    rateDen: head.net,
    slippageBps: p.slippageBps,
    validitySeconds: head.validity_seconds,
    builderFeeBps: head.builder_fee_bps,
    protocolFeeBps: head.protocol_fee_bps,
    ...(p.inputs ? { inputs: p.inputs.map((i) => ({ mint: i.token.addr, amount: i.amount })) } : {}),
  });
  p.log("signing the open with your Jupnet key (the api pays its fee)…");
  p.phase?.("sign");
  const signed = signJupnetOpen(jupnetSigning(built.tx, p.user), key);
  p.phase?.("submit");
  const { tx: sig, intentHash: submitted } = await submit(p.api, p.src.key, signed);
  if (submitted) return { intentHash: submitted, openTx: sig, quote: best, head, built };
  p.log(`open sent (${sig}); finding its intent…`);
  p.phase?.("confirm");
  const intentHash = await awaitRegistered(p.api, p.src.key, sig);
  if (!intentHash) throw new Error(`could not find the intent for ${sig}; it may still land, track it by hash once it shows`);
  return { intentHash, openTx: sig, quote: best, head, built };
}
